import { firstUpperCaseLetter } from '@/lib/utils';
import approvalDelegationIcon from '@/resources/approval_delegation.svg';
import familyStarIcon from '@/resources/family_star.svg';
import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { CalendarDays, ChevronDown } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import { getLanguage } from '@/services/GRNLwithoutDep';
import { translate } from '@/services/TranslationService';
import { NovovilleButton, Typography } from '@/components/novoville';
import NovovilleRating from '@/components/novoville/NovovilleRating';
import { toast } from '@/components/ui/use-toast';
import { withNOVOHeader } from '../sideMenu/HeaderHOC';
import CalendarEventError from './CalendarEventError';
import CalendarEventLoading from './CalendarEventLoading';
import { getCalendarEvent, postAttendEvent, postRating } from './actions';

const DEFAULT_IMAGE = '/images/img/Default_image_messages.png';

const CalendarEvent = (props) => {
  const dispatch = useDispatch();

  const [imageSrc, setImageSrc] = useState(DEFAULT_IMAGE);

  const { isLoading, error, calendarEvent } = useSelector(
    (state) => state.calendarReducer
  );

  const {
    id,
    photo,
    body,
    start_at,
    end_at,
    title,
    type,
    address,
    authority_name,
    user_can_attend,
    attend_actions_user_count,
    rate_actions_user_count,
    user_can_rate,
  } = calendarEvent || {};

  const { eventId } = props.match.params;

  const { setHeaderTitle } = props;
  const [language, setLanguage] = useState(getLanguage());

  const isVolunteerEvent = type === 'volunteer_event' || type === 'volunteer';

  const hasFinished = !!end_at && dayjs(end_at).isBefore(dayjs());

  const readyToRateEvent = hasFinished && !!user_can_rate;

  const [isEventAttended, setIsEventAttended] = useState(
    attend_actions_user_count > 0 && !hasFinished && user_can_attend
  );

  const [isEventRated, setIsEventRated] = useState(
    user_can_rate && rate_actions_user_count > 0 && hasFinished
  );

  const [rating, setRating] = useState(-1);

  const [collapsed, setCollapsed] = useState(hasFinished && !isEventRated);

  const [isStateLoading, setIsStateLoading] = useState(true);

  const attendEvent = useCallback(() => {
    dispatch(postAttendEvent(authority_name, eventId)).then(() => {
      setIsEventAttended(true);
    });
  }, [authority_name, dispatch, eventId]);

  const rateEvent = useCallback(() => {
    dispatch(postRating(authority_name, eventId, rating)).then(() => {
      if (rating > 0) {
        setIsEventRated(true);
        toast({
          title: translate('OTHER.success'),
          description: translate(
            'Your rating has been submitted. Thank you for your feedback!'
          ),
          variant: 'success',
        });
      }
      setIsEventRated(true);
    });
  }, [authority_name, dispatch, eventId, rating]);

  useEffect(() => {
    setHeaderTitle(translate('sideMenu.Calendar'));
    setLanguage(getLanguage());

    setIsStateLoading(true);
    dispatch(getCalendarEvent(eventId)).then(() => {
      setIsStateLoading(false);
    });
  }, [dispatch, language]);

  useEffect(() => {
    // Due to rerendering issues state changes are not reflected immediately

    setIsEventAttended(
      attend_actions_user_count > 0 && !hasFinished && user_can_attend
    );
    setIsEventRated(
      user_can_rate && rate_actions_user_count > 0 && hasFinished
    );
  }, [calendarEvent?.id]);

  return (
    <div id={id} className="flex flex-1 flex-col relative">
      {isLoading && isStateLoading && (
        <div className="absolute flex-1 w-full h-full bg-black bg-opacity-5 z-50 pointer-events-none">
          <CalendarEventLoading />
        </div>
      )}
      {error && <CalendarEventError />}
      {calendarEvent && (
        <div
          className="md:m-10 m-2 flex bg-white flex-col lg:flex-row h-full md:h-fit
                    md:rounded-xl overflow-y-auto md:p-8 p-2
                    transition-all duration-500 ease-in-out shadow-lg animate-[heightChange_0.6s_ease]"
        >
          {photo && (
            <div className="md:flex-1 w-full flex min-w-80 items-start justify-start">
              <img
                src={imageSrc}
                alt={title}
                className="sm:max-h-72 max-h-56 mb-8 md:mx-3 mx-auto object-contain"
                loading="lazy"
                onLoad={() => setImageSrc(photo || DEFAULT_IMAGE)}
                onError={() => setImageSrc(DEFAULT_IMAGE)}
              />
            </div>
          )}
          <div className="flex w-full flex-col space-y-2">
            <div className="flex md:flex-row flex-col justify-between py-2 border-b-[1px] border-gray-300">
              <div className="flex  flex-row space-x-1 items-center space-y-2">
                <CalendarDays size={24} className="text-purpleLink" />
                <Typography variant={'semibold_16'}>
                  {firstUpperCaseLetter(translate(type) || translate('event'))}
                </Typography>
              </div>
              <Typography variant={'regular_16'} className="text-gray-500 ">
                {dayjs(start_at).format('dddd, DD MMM YYYY')}
              </Typography>
            </div>
            <div className="flex item-center justify-between">
              <Typography variant={'bold_20'} className="text-gray-800">
                {title}
              </Typography>
              {isVolunteerEvent && readyToRateEvent && (
                <button
                  className="text-gray-500"
                  onClick={() => setCollapsed(!collapsed)}
                >
                  <span className="text-gray-500 hidden md:inline">
                    {collapsed
                      ? translate('Read More')
                      : translate('Show Less')}
                  </span>
                  {collapsed ? (
                    <ChevronDown
                      size={20}
                      className="inline ml-1 transition-transform duration-300"
                    />
                  ) : (
                    <ChevronDown
                      size={20}
                      className="inline ml-1 transition-transform duration-300 transform rotate-180"
                    />
                  )}
                </button>
              )}
            </div>
            {isVolunteerEvent && isEventAttended && (
              <div className="flex items-center space-x-2 bg-green-100 p-4 rounded-md">
                <img
                  src={approvalDelegationIcon}
                  alt="You will attend icon"
                  className="w-6 h-5"
                />
                <Typography variant={'regular_16'} className="text-gray-800">
                  {translate('You will attend this volunteering event')}
                </Typography>
              </div>
            )}
            {isVolunteerEvent && isEventRated && (
              <div className="flex items-center space-x-2 bg-green-100 p-4 rounded-md">
                <img
                  src={familyStarIcon}
                  alt="You have rated icon"
                  className="w-6 h-5"
                />
                <Typography variant={'regular_16'} className="text-gray-800">
                  {translate('You have already rated this event.')}
                </Typography>
              </div>
            )}
            {isVolunteerEvent &&
              hasFinished &&
              attend_actions_user_count === 0 && (
                <div className="flex items-center space-x-2 bg-gray-100 p-4 rounded-md">
                  <Typography variant={'regular_16'} className="text-gray-800">
                    {translate('This volunteering event has expired.')}
                  </Typography>
                </div>
              )}
            {isVolunteerEvent && (
              <div className="flex flex-col space-y-1">
                <Typography variant={'regular_16'} className="text-gray-500">
                  {translate('Location')}: <strong>{address || '-'}</strong>
                </Typography>
                <Typography variant={'regular_16'} className="text-gray-500">
                  {translate('Starts:')}{' '}
                  <strong>{dayjs(start_at).format('DD/MM/YY, HH:mm')}</strong>
                </Typography>
              </div>
            )}
            <div
              className={`transition-all duration-500 ease-in-out overflow-hidden ${
                collapsed ? 'max-h-0 opacity-70' : 'max-h-fit opacity-100'
              }`}
            >
              <Typography variant={'regular_16'}>{body}</Typography>
            </div>
            {isVolunteerEvent &&
              !hasFinished &&
              user_can_attend &&
              !isEventAttended && (
                <div>
                  <NovovilleButton
                    className="mt-4 md:max-w-28 "
                    variant="primary"
                    disabled={!isVolunteerEvent}
                    onClick={attendEvent}
                  >
                    {translate('I will attend')}
                  </NovovilleButton>
                </div>
              )}
            {isVolunteerEvent && readyToRateEvent && !isEventRated && (
              <div className="flex flex-col space-y-6 py-6">
                <div className="flex items-center space-x-2 bg-orange-300 p-4 rounded-md">
                  <img
                    src={familyStarIcon}
                    alt="You can rate icon"
                    className="w-6 h-5"
                  />
                  <Typography variant={'regular_16'} className="text-gray-800">
                    {translate('Please rate the event you attended.')}
                  </Typography>
                </div>
                <div className="flex flex-col space-y-3 pt-2">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="event_feedback"
                      value="did_attend"
                      onChange={() => {
                        setRating(-1);
                      }}
                      checked={rating === -1}
                      className="form-radio h-5 w-5 accent-black focus:ring-0 border-gray-400"
                    />
                    <span className="ml-3 text-gray-800 text-base">
                      {translate('I did not manage to attend')}
                    </span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="event_feedback"
                      value="rate_event"
                      className="form-radio h-5 w-5 accent-black focus:ring-0 border-gray-400"
                      onChange={() => {
                        setRating(0);
                      }}
                      checked={rating !== -1}
                    />
                    <span className="ml-3 text-gray-800 text-base">
                      {translate('Rate the event')}
                    </span>
                  </label>
                </div>
                <NovovilleRating
                  rating={rating}
                  onChange={(newRating) => {
                    setRating(newRating);
                  }}
                  disabled={rating === -1}
                />
                <NovovilleButton
                  className="md:max-w-20 w-full "
                  variant="primary"
                  onClick={rateEvent}
                >
                  {translate('SUBMIT')}
                </NovovilleButton>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default withNOVOHeader(withRouter(CalendarEvent));
